import React, { useState } from 'react';
import { fmt } from '../../../design/format';
import { ChartTable, TipRow, TipTitle, useChartTip, useChartWidth } from './core';

/**
 * Layered left-to-right Sankey built from call lineage (mockup VL.charts.sankey).
 * nodes: [{ name, col, color, kind? }], links: [{ s, t, v, color? }] (s/t are node indexes)
 */
export function Sankey({ nodes: nodeDefs, links: linkDefs, height = 440, labelSpace = 170, colTitles = [], aria = 'Call flow', showTable = false }) {
  const [ref, W] = useChartWidth(560);
  const tip = useChartTip();
  const [hoverLink, setHoverLink] = useState(null);

  if (showTable) {
    return (
      <div className="chart" ref={ref}>
        <ChartTable head={['From', 'To', 'Calls']} rows={linkDefs.map((l) => [nodeDefs[l.s].name, nodeDefs[l.t].name, fmt.int(l.v)])} />
      </div>
    );
  }

  const H = height;
  const nodes = nodeDefs.map((n, i) => ({ ...n, i, in: 0, out: 0 }));
  linkDefs.forEach((l) => {
    nodes[l.s].out += l.v;
    nodes[l.t].in += l.v;
  });
  nodes.forEach((n) => {
    n.val = Math.max(n.in, n.out);
  });
  const cols = Math.max(...nodes.map((n) => n.col)) + 1;
  const nodeW = 12;
  const padTop = 26;
  const pw = W - labelSpace - 8;
  const colX = (c) => 8 + (c * (pw - nodeW)) / Math.max(1, cols - 1);
  const gap = 14;
  let scale = Infinity;
  for (let c = 0; c < cols; c += 1) {
    const colNodes = nodes.filter((n) => n.col === c);
    const tot = colNodes.reduce((a, n) => a + n.val, 0);
    if (tot > 0) scale = Math.min(scale, (H - padTop - 8 - gap * (colNodes.length - 1)) / tot);
  }
  if (!Number.isFinite(scale)) scale = 1;
  for (let c = 0; c < cols; c += 1) {
    let yy = padTop;
    nodes
      .filter((n) => n.col === c)
      .forEach((n) => {
        n.x = colX(c);
        n.y = yy;
        n.h = Math.max(2, n.val * scale);
        yy += n.h + gap;
        n.so = 0;
        n.to = 0;
      });
  }
  const links = linkDefs.map((l) => {
    const a = nodes[l.s];
    const b = nodes[l.t];
    const w = l.v * scale;
    const y0 = a.y + a.so + w / 2;
    const y1 = b.y + b.to + w / 2;
    a.so += w;
    b.to += w;
    const x0 = a.x + nodeW;
    const x1 = b.x;
    const mx = (x0 + x1) / 2;
    return { ...l, d: `M${x0} ${y0}C${mx} ${y0} ${mx} ${y1} ${x1} ${y1}`, w, color: l.color || a.color };
  });

  return (
    <div className="chart" ref={ref}>
      <svg viewBox={`0 0 ${W} ${H}`} height={H} role="img" aria-label={aria}>
        {colTitles.map((t, c) => (
          <text key={t} className="tick" x={colX(c)} y={12}>
            {t}
          </text>
        ))}
        {links.map((l, li) => (
          <path
            key={li}
            d={l.d}
            style={{ fill: 'none', stroke: l.color, strokeOpacity: hoverLink === li ? 0.55 : 0.28, strokeWidth: Math.max(1, l.w) }}
            onMouseMove={(ev) => {
              setHoverLink(li);
              tip.show(
                <>
                  <TipTitle>{`${nodes[l.s].name} to ${nodes[l.t].name}`}</TipTitle>
                  <TipRow color={l.color} label="Calls" value={fmt.int(l.v)} />
                  <TipRow color="transparent" label="Share of source" value={fmt.pct((l.v / nodes[l.s].out) * 100)} />
                </>,
                ev.clientX,
                ev.clientY
              );
            }}
            onMouseLeave={() => {
              setHoverLink(null);
              tip.hide();
            }}
          />
        ))}
        {nodes.map((n) => (
          <React.Fragment key={n.i}>
            <rect
              x={n.x}
              y={n.y}
              width={nodeW}
              height={n.h}
              rx={2}
              style={{ fill: n.color }}
              onMouseMove={(ev) =>
                tip.show(
                  <>
                    <TipTitle>{n.name}</TipTitle>
                    <TipRow color={n.color} label={n.kind || 'Calls'} value={fmt.int(n.val)} />
                  </>,
                  ev.clientX,
                  ev.clientY
                )
              }
              onMouseLeave={tip.hide}
            />
            <text className="lbl-strong" x={n.x + nodeW + 6} y={n.y + Math.min(n.h / 2, 14) + 4} textAnchor="start" style={{ paintOrder: 'stroke', stroke: 'var(--surface)', strokeWidth: '3px' }}>
              {n.name}
              <tspan className="tick" dx={6} style={{ fontWeight: 400 }}>
                {fmt.int(n.val)}
              </tspan>
            </text>
          </React.Fragment>
        ))}
      </svg>
      {tip.node}
    </div>
  );
}
